import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useContact, useDeleteContact, useUpdateContact } from '../../api/contacts';
import { useSetContactTags } from '../../api/tags';
import { useSetContactCustomFields } from '../../api/custom-fields';
import { TaskList } from '../tasks/TaskList';
import { NoteList } from '../notes/NoteList';
import { TagPicker } from '../tags/TagPicker';
import { FollowUpList } from '../follow-ups/FollowUpList';
import { ActivityTimeline } from '../activities/ActivityTimeline';
import { CustomFieldValues } from '../custom-fields/CustomFieldValues';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt style={{ color: 'var(--color-text-muted)' }}>{label}</dt>
      <dd style={{ margin: 0 }}>{value || '—'}</dd>
    </>
  );
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const { data: contact, isPending, isError, error, refetch } = useContact(id);
  const del = useDeleteContact();
  const update = useUpdateContact(id ?? '');
  const setTags = useSetContactTags(id ?? '');
  const setCustomFields = useSetContactCustomFields(id ?? '');

  if (isPending) return <PageState kind="loading" message="Loading contact…" />;
  if (isError) {
    return error.code === 'CONTACT_NOT_FOUND' ? (
      <PageState kind="empty" title="Contact not found" message="It may have been deleted." />
    ) : (
      <PageState kind="error" message={error.message} onRetry={() => void refetch()} />
    );
  }

  async function onDelete() {
    if (!contact) return;
    if (!window.confirm(`Delete "${contact.displayName}"? This cannot be undone.`)) return;
    try {
      await del.mutateAsync(contact.id);
      toast.success('Contact deleted.');
      navigate('/contacts', { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the contact.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{contact.displayName}</h1>
        <span style={{ flex: 1 }} />
        {hasPermission('contacts.update') ? (
          <Button variant="secondary" onClick={() => navigate(`/contacts/${contact.id}/edit`)}>
            Edit
          </Button>
        ) : null}
        {hasPermission('contacts.delete') ? (
          <Button variant="secondary" onClick={() => void onDelete()} disabled={del.isPending}>
            Delete
          </Button>
        ) : null}
      </div>

      <Card>
        <dl
          style={{
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 'var(--space-2) var(--space-4)',
          }}
        >
          <Row label="Kind" value={contact.kind} />
          <Row label="Status" value={<span className="ne-badge">{contact.status}</span>} />
          <Row label="First name" value={contact.firstName} />
          <Row label="Last name" value={contact.lastName} />
          <Row label="Company" value={contact.companyName} />
          <Row label="Email" value={contact.email} />
          <Row label="Phone" value={contact.phone} />
          <Row label="Alternate phone" value={contact.alternatePhone} />
          <Row label="Source" value={contact.source} />
          <Row label="Created" value={new Date(contact.createdAt).toLocaleString()} />
          <Row label="Updated" value={new Date(contact.updatedAt).toLocaleString()} />
        </dl>
      </Card>

      <Card>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <strong>Marketing messages</strong>
            <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
              {contact.marketingOptOut
                ? `Opted out${contact.marketingOptOutAt ? ` on ${new Date(contact.marketingOptOutAt).toLocaleString()}` : ''} - excluded from campaigns.`
                : 'Opted in - eligible for marketing campaigns.'}
            </p>
          </div>
          {hasPermission('contacts.update') ? (
            <Button
              variant="secondary"
              disabled={update.isPending}
              onClick={async () => {
                try {
                  await update.mutateAsync({ marketingOptOut: !contact.marketingOptOut });
                  toast.success(
                    contact.marketingOptOut ? 'Opted back in.' : 'Opted out of marketing.',
                  );
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Could not update.');
                }
              }}
            >
              {contact.marketingOptOut ? 'Opt back in' : 'Opt out'}
            </Button>
          ) : null}
        </div>
      </Card>

      <TagPicker tags={contact.tags} onSave={(tagIds) => setTags.mutateAsync({ tagIds })} />

      <CustomFieldValues
        entityType="CONTACT"
        values={contact.customFields}
        onSave={(values) => setCustomFields.mutateAsync({ values })}
      />

      <TaskList
        query={{ contactId: contact.id }}
        attachTo={{ contactId: contact.id }}
        title="Tasks"
      />

      <FollowUpList query={{ contactId: contact.id }} attachTo={{ contactId: contact.id }} />

      <NoteList query={{ contactId: contact.id }} attachTo={{ contactId: contact.id }} />

      <ActivityTimeline query={{ contactId: contact.id }} attachTo={{ contactId: contact.id }} />

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/contacts">← All contacts</Link>
      </p>
    </div>
  );
}
